import { useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { decodeToken } from 'react-jwt'
import { CodeResponse, useGoogleLogin } from '@react-oauth/google'
import { PlaybackCountryCodeMap } from '@playback-rewards/shared-libraries'

//constants
import { LOCAL_STORAGE, REACT_NATIVE_STATUS, USER_PLATFORM } from 'constants/index'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
import NativeAppContext from 'context/NativeApp/NativeAppContext'
//services
import { authorize, getAuthLinkWithScopes } from 'services/authService'
//utils
import { getUserPlatform, getUserTimezone, handleError, sendRNMessage } from 'utils/utils'
import useGeoDataQuery from './useGeoDataQuery'
import { resolveGeoAccess, resolveNativeAuthorizationAction } from './authEntry'

const useAuthorization = (isBanned?: boolean, signUpParams?: string) => {
  const { login } = useContext(AuthContext)
  const { showLoader, hideLoader } = useContext(LoaderContext)
  const { nativeAppDetails } = useContext(NativeAppContext)
  const navigate = useNavigate()
  const { geoData } = useGeoDataQuery()

  const trackSignupEvent = useCallback((eventName: string) => {
    if (!window.ReactNativeWebView) return

    sendRNMessage({
      status: REACT_NATIVE_STATUS.SEND_EVENT_APPSFLYER,
      eventName,
      eventValue: { signup_entry_point: window.location.pathname || 'unknown' },
    })
  }, [])

  const checkAccessByGeo = useCallback((): { ip: string; country: string | null } | null => {
    const { userGeoInfo, blockedCountryCode } = resolveGeoAccess(geoData, PlaybackCountryCodeMap)

    if (blockedCountryCode) {
      navigate(`/country-error?detected-country=${blockedCountryCode}`)
      return null
    }

    return userGeoInfo
  }, [geoData, navigate])

  const handleAuthorizationNative = useCallback(async () => {
    const userGeoInfo = checkAccessByGeo()
    const referralId = sessionStorage.getItem(LOCAL_STORAGE.REFERRAL_ID) || null

    const action = resolveNativeAuthorizationAction({
      isBanned,
      userGeoInfo,
      referralId,
      versionCode: nativeAppDetails?.versionCode,
    })

    if (action.redirectTo) {
      navigate(action.redirectTo)
      return
    }

    if (action.message) {
      return sendRNMessage({
        ...action.message,
        signupEntryPoint: window.location.pathname || 'unknown',
      })
    }
  }, [checkAccessByGeo, isBanned, nativeAppDetails?.versionCode, navigate])

  const handleGoogleLoginSuccess = async (codeResponse: CodeResponse, isBanned: boolean) => {
    try {
      showLoader()

      if (isBanned) {
        hideLoader()
        navigate('/blocked')
        return
      }

      const userGeoInfo = checkAccessByGeo()
      if (!userGeoInfo) {
        hideLoader()
        return
      }

      const authResp = await authorize({
        platform: getUserPlatform(USER_PLATFORM.ANDROID),
        IPAddress: userGeoInfo.ip,
        authorizationCode: codeResponse.code,
        referrerId: sessionStorage.getItem(LOCAL_STORAGE.REFERRAL_ID) || null,
        signupAttr: signUpParams || null,
        country: userGeoInfo.country,
        signUpIPCountry: userGeoInfo.country,
        redirectUri: 'postmessage',
        timezone: getUserTimezone(),
      })

      localStorage.setItem(LOCAL_STORAGE.JWT_TOKEN, authResp.accessToken)

      if (authResp.type === 'login') {
        await login(authResp.accessToken, true)
        hideLoader()
      } else {
        hideLoader()
        if (!codeResponse.scope.includes('https://www.googleapis.com/auth/gmail.readonly')) {
          const decodedToken = decodeToken(authResp.accessToken)

          const email = (decodedToken as { email: string }).email

          setTimeout(
            () =>
              getAuthLinkWithScopes(email, '').then(
                (authLink) => (window.location.href = authLink as unknown as string)
              ),
            100
          )
        } else {
          navigate('/platform-select')
        }
      }
    } catch (err: any) {
      hideLoader()
      handleError(err)
    }
  }

  const handleAuthorizationWeb = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const isBanned = false // You should pass this from the component if needed
      await handleGoogleLoginSuccess(codeResponse, isBanned)
    },
    onError: () => new Error('No authorization code received'),
    scope:
      'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.readonly',
    flow: 'auth-code',
  })

  const handleAuthorization = window.ReactNativeWebView
    ? handleAuthorizationNative
    : handleAuthorizationWeb
  const trackSignupScreenView = useCallback(
    () => trackSignupEvent('signup_screen_view'),
    [trackSignupEvent]
  )

  return {
    handleAuthorization,
    handleSignupAuthorization: () => {
      trackSignupEvent('signup_google_tap')
      return handleAuthorization()
    },
    trackSignupScreenView,
  }
}

export default useAuthorization
